import type { ColumnDef } from '@tanstack/react-table'
import { DataTableColumnHeader } from '@/shared/components/data-table'
import { cn } from '@/shared/lib/utils'
import { formatHumanDate } from '@/shared/utils/format-date'
import type { CompanyAdminRow } from '../models/company-admin'

export const COMPANIES_ADMIN_COLUMN_NAMES: Record<string, string> = {
  name: 'Empresa',
  giinNumber: 'GIIN',
  type: 'Tipo',
  active: 'Estado',
  updatedAt: 'Actualizado'
}

/**
 * Badge sobrio estilo Shopify: píldora neutra (gris) con un punto de color
 * discreto — el color vive en el punto, no grita en el fondo.
 */
export function CompanyActiveBadge({ active }: { active: boolean }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/80">
      <span
        aria-hidden="true"
        className={cn(
          'h-1.5 w-1.5 shrink-0 rounded-full',
          active ? 'bg-emerald-500' : 'bg-neutral-400'
        )}
      />
      {active ? 'Activa' : 'Inactiva'}
    </span>
  )
}

export function CompanyTypeBadge({ type }: { type: string }) {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/70">
      {type}
    </span>
  )
}

/** Píldora discreta para la ÚNICA empresa predeterminada del sistema. */
export function CompanyDefaultBadge() {
  return (
    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-xs font-medium text-foreground/70">
      Predeterminada
    </span>
  )
}

export const companiesAdminColumns: ColumnDef<CompanyAdminRow>[] = [
  {
    accessorKey: 'name',
    header: ({ column }) => (
      <DataTableColumnHeader className="min-w-[240px]" column={column} name="Empresa" />
    ),
    cell: ({ row }) => (
      <span className="flex items-center gap-2">
        <span className="truncate font-medium" title={row.original.name}>
          {row.original.name}
        </span>
        {row.original.default && <CompanyDefaultBadge />}
      </span>
    )
  },
  {
    accessorKey: 'giinNumber',
    header: ({ column }) => <DataTableColumnHeader column={column} name="GIIN" sortable={false} />,
    cell: ({ row }) =>
      row.original.giinNumber ? (
        <span className="whitespace-nowrap tabular-nums">{row.original.giinNumber}</span>
      ) : (
        <span className="text-xs text-muted-foreground">—</span>
      )
  },
  {
    accessorKey: 'type',
    header: ({ column }) => <DataTableColumnHeader column={column} name="Tipo" sortable={false} />,
    cell: ({ row }) => <CompanyTypeBadge type={row.original.type} />
  },
  {
    accessorKey: 'active',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Estado" sortable={false} />
    ),
    cell: ({ row }) => <CompanyActiveBadge active={row.original.active} />
  },
  {
    accessorKey: 'updatedAt',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} name="Actualizado" sortable={false} />
    ),
    cell: ({ row }) => (
      <span className="whitespace-nowrap text-muted-foreground">
        {formatHumanDate(row.original.updatedAt)}
      </span>
    )
  }
]
